import { createContext, useContext, useMemo, useState, useEffect } from "react";

interface OrganizationContextValue {
  organizationId: string | null;
  organizationName: string | null;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

const readOrgFromStorage = (): OrganizationContextValue => {
  try {
    const authData = localStorage.getItem("customAuth");
    if (authData) {
      const parsed = JSON.parse(authData);
      return {
        organizationId: parsed.organizationId ?? null,
        organizationName: parsed.organizationName ?? null,
      };
    }
  } catch {}
  return { organizationId: null, organizationName: null };
};

export const OrganizationProvider = ({ children }: { children: React.ReactNode }) => {
  const [orgState, setOrgState] = useState<OrganizationContextValue>(readOrgFromStorage);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "customAuth") {
        setOrgState(readOrgFromStorage());
      }
    };
    window.addEventListener("storage", handleStorage);

    // Also re-read on mount in case login happened in this tab
    const interval = setInterval(() => {
      const current = readOrgFromStorage();
      setOrgState(prev => {
        if (prev.organizationId !== current.organizationId) return current;
        return prev;
      });
    }, 1000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, []);

  return (
    <OrganizationContext.Provider value={orgState}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizationContext = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganizationContext must be used within an OrganizationProvider");
  }
  return context;
};
